/**
 * ContractSettingsSection Component
 *
 * Contract-specific settings including notification preferences
 * and default values for new contracts.
 * Only visible to Rise+ tier users with contracts enabled.
 */

import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  Skeleton,
  Divider,
  Chip,
} from '@mui/material';
import {
  Notifications,
  Description,
  Schedule,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contractsApi, ContractSettings } from '../../services/contractsApi';
import { useAuth } from '../../contexts/AuthContext';

export default function ContractSettingsSection() {
  const { organization } = useAuth();
  const queryClient = useQueryClient();
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if contracts are enabled
  const contractsEnabled = organization?.boldsign?.contractsEnabled === true;

  // Fetch current settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ['contractSettings'],
    queryFn: contractsApi.getSettings,
    enabled: contractsEnabled,
  });

  // Update settings mutation
  const updateSettings = useMutation({
    mutationFn: contractsApi.updateSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contractSettings'] });
      setSuccess(true);
      setError(null);
      setTimeout(() => setSuccess(false), 3000);
    },
    onError: (err: Error) => {
      setError(err.message || 'Failed to update settings');
      setSuccess(false);
    },
  });

  // Local state for form values
  const [localSettings, setLocalSettings] = useState<Partial<ContractSettings>>({});

  // Get the effective value (local override or fetched value)
  const getValue = <K extends keyof ContractSettings>(key: K): ContractSettings[K] => {
    if (key in localSettings) {
      return localSettings[key] as ContractSettings[K];
    }
    return settings?.[key] as ContractSettings[K];
  };

  const handleToggle = (key: keyof ContractSettings) => {
    const currentValue = getValue(key);
    setLocalSettings((prev) => ({
      ...prev,
      [key]: !currentValue,
    }));
  };

  const handleNumberChange = (key: keyof ContractSettings, value: number) => {
    setLocalSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = () => {
    if (Object.keys(localSettings).length > 0) {
      updateSettings.mutate(localSettings);
      setLocalSettings({});
    }
  };

  const hasChanges = Object.keys(localSettings).length > 0;

  // If contracts are not enabled, show info message
  if (!contractsEnabled) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Description color="action" />
            <Typography variant="h6">Contract Settings</Typography>
            <Chip label="Not Enabled" size="small" color="default" />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Contract settings will be available once the contracts feature is enabled for your organization.
            Please contact support if you believe this should be enabled.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width={200} height={32} />
          <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={3}>
          <Description color="primary" />
          <Typography variant="h6">Contract Settings</Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Contract settings updated successfully!
          </Alert>
        )}

        {/* Notification Preferences */}
        <Box mb={3}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Notifications fontSize="small" color="action" />
            <Typography variant="subtitle1" fontWeight={500}>
              Email Notifications
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Choose which contract events trigger email notifications.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={getValue('notifyOnViewed') ?? true}
                    onChange={() => handleToggle('notifyOnViewed')}
                  />
                }
                label="Contract Viewed"
              />
              <Typography variant="caption" color="text.secondary" display="block" ml={6}>
                Notify when a client views the contract
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={getValue('notifyOnSigned') ?? true}
                    onChange={() => handleToggle('notifyOnSigned')}
                  />
                }
                label="Contract Signed"
              />
              <Typography variant="caption" color="text.secondary" display="block" ml={6}>
                Notify when a client signs the contract
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={getValue('notifyOnDeclined') ?? true}
                    onChange={() => handleToggle('notifyOnDeclined')}
                  />
                }
                label="Contract Declined"
              />
              <Typography variant="caption" color="text.secondary" display="block" ml={6}>
                Notify when a client declines the contract
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={getValue('notifyOnExpired') ?? true}
                    onChange={() => handleToggle('notifyOnExpired')}
                  />
                }
                label="Contract Expired"
              />
              <Typography variant="caption" color="text.secondary" display="block" ml={6}>
                Notify when a contract expires without signature
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Default Settings */}
        <Box mb={3}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Schedule fontSize="small" color="action" />
            <Typography variant="subtitle1" fontWeight={500}>
              Default Contract Settings
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            These defaults apply when creating new contracts.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Default Expiration (days)"
                type="number"
                value={getValue('defaultExpirationDays') ?? 30}
                onChange={(e) =>
                  handleNumberChange('defaultExpirationDays', parseInt(e.target.value, 10) || 30)
                }
                inputProps={{ min: 1, max: 365 }}
                fullWidth
                helperText="How long clients have to sign (1-365 days)"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Reminder Interval (days)"
                type="number"
                value={getValue('defaultReminderDays') ?? 3}
                onChange={(e) =>
                  handleNumberChange('defaultReminderDays', parseInt(e.target.value, 10) || 3)
                }
                inputProps={{ min: 1, max: 30 }}
                fullWidth
                helperText="Days between automatic reminders (1-30)"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={getValue('autoRemindersEnabled') ?? true}
                    onChange={() => handleToggle('autoRemindersEnabled')}
                  />
                }
                label="Enable Automatic Reminders"
              />
              <Typography variant="caption" color="text.secondary" display="block" ml={6}>
                Automatically send reminder emails to clients who haven't signed
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Save Button */}
        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!hasChanges || updateSettings.isPending}
          >
            {updateSettings.isPending ? 'Saving...' : 'Save Contract Settings'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
